import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Calendar, Clock, User, Eye, Trash2, MessageSquare } from 'lucide-react';
import apiService from '../services/api';
import { ContactSubmission } from '../types';
import toast from 'react-hot-toast';

interface ContactFilters {
  status: string;
  search: string;
}

const Contact: React.FC = () => {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState<ContactFilters>({
    status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 10
  });

  useEffect(() => {
    fetchSubmissions();
  }, [filters.status, pagination.current_page]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await apiService.getContactSubmissions(
        pagination.current_page,
        pagination.items_per_page,
        filters.status || undefined
      );
      
      // Filter by search term if provided
      let filteredSubmissions = response.submissions;
      if (filters.search) {
        filteredSubmissions = response.submissions.filter(sub => 
          sub.name.toLowerCase().includes(filters.search.toLowerCase()) ||
          sub.email.toLowerCase().includes(filters.search.toLowerCase()) ||
          sub.message.toLowerCase().includes(filters.search.toLowerCase()) ||
          sub.event_type?.toLowerCase().includes(filters.search.toLowerCase())
        );
      }
      
      setSubmissions(filteredSubmissions);
      setPagination(response.pagination);
    } catch (error: any) {
      toast.error('Failed to fetch contact submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id: number, status: string, adminNotes?: string) => {
    try {
      const updatedSubmission = await apiService.updateContactSubmission(id, { 
        status, 
        admin_notes: adminNotes 
      });
      setSubmissions(prev => prev.map(sub => 
        sub.id === id ? updatedSubmission : sub
      ));
      toast.success('Status updated successfully');
    } catch (error: any) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this submission?')) {
      try {
        await apiService.deleteContactSubmission(id);
        setSubmissions(prev => prev.filter(sub => sub.id !== id));
        toast.success('Submission deleted successfully');
      } catch (error: any) {
        toast.error('Failed to delete submission');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'contacted':
        return 'bg-yellow-100 text-yellow-800';
      case 'booked':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEventTypeIcon = (eventType?: string) => {
    switch (eventType?.toLowerCase()) {
      case 'wedding':
        return '💒';
      case 'birthday':
        return '🎂';
      case 'corporate':
        return '🏢';
      case 'graduation':
        return '🎓';
      case 'baby shower':
        return '👶';
      default:
        return '🎉';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contact Submissions</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage customer inquiries and event requests
          </p>
        </div>
        <div className="text-sm text-gray-500">
          {submissions.length} submissions total
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by name, email, or message..."
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">All Status</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="booked">Booked</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Submissions List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {submissions.map((submission, index) => (
            <motion.li
              key={submission.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="hover:bg-gray-50"
            >
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-primary-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <h3 className="text-sm font-medium text-gray-900 mr-2">
                          {submission.name}
                        </h3>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(submission.status)}`}
                        >
                          {submission.status}
                        </span>
                        {submission.event_type && (
                          <span className="ml-2 text-lg">
                            {getEventTypeIcon(submission.event_type)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center text-sm text-gray-500 mt-1">
                        <Mail className="flex-shrink-0 mr-1.5 h-4 w-4" />
                        <span className="mr-4">{submission.email}</span>
                        {submission.phone && (
                          <>
                            <Phone className="flex-shrink-0 mr-1.5 h-4 w-4" />
                            <span className="mr-4">{submission.phone}</span>
                          </>
                        )}
                        {submission.event_type && (
                          <>
                            <MessageSquare className="flex-shrink-0 mr-1.5 h-4 w-4" />
                            <span className="mr-4">{submission.event_type}</span>
                          </>
                        )}
                        {submission.event_date && (
                          <>
                            <Calendar className="flex-shrink-0 mr-1.5 h-4 w-4" />
                            <span>{new Date(submission.event_date).toLocaleDateString()}</span>
                          </>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                        {submission.message}
                      </p>
                      {submission.admin_notes && (
                        <div className="mt-2 p-2 bg-yellow-50 rounded-md">
                          <p className="text-xs text-yellow-800">
                            <strong>Admin Notes:</strong> {submission.admin_notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setSelectedSubmission(submission);
                        setShowModal(true);
                      }}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      View
                    </button>
                    
                    {/* Quick status updates */}
                    {submission.status === 'new' && (
                      <button
                        onClick={() => handleStatusUpdate(submission.id, 'contacted')}
                        className="inline-flex items-center px-3 py-1 border border-yellow-300 shadow-sm text-xs font-medium rounded text-yellow-700 bg-white hover:bg-yellow-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                      >
                        Mark Contacted
                      </button>
                    )}
                    
                    {submission.status === 'contacted' && (
                      <button
                        onClick={() => handleStatusUpdate(submission.id, 'booked')}
                        className="inline-flex items-center px-3 py-1 border border-green-300 shadow-sm text-xs font-medium rounded text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        Mark Booked
                      </button>
                    )}
                    
                    <button
                      onClick={() => handleDelete(submission.id)}
                      className="inline-flex items-center px-3 py-1 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>

                {/* Timestamp */}
                <div className="mt-2 flex items-center text-xs text-gray-400">
                  <Clock className="flex-shrink-0 mr-1 h-3 w-3" />
                  <span>Submitted: {new Date(submission.created_at).toLocaleString()}</span>
                  {submission.updated_at !== submission.created_at && (
                    <>
                      <span className="mx-2">•</span>
                      <span>Updated: {new Date(submission.updated_at).toLocaleString()}</span>
                    </>
                  )}
                </div>
              </div>
            </motion.li>
          ))}
        </ul>
      </div>

      {/* Empty State */}
      {submissions.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <Mail className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No submissions</h3>
          <p className="mt-1 text-sm text-gray-500">
            Contact submissions will appear here when customers reach out.
          </p>
        </motion.div>
      )}

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
            disabled={pagination.current_page === 1}
            className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-3 py-1">
            Page {pagination.current_page} of {pagination.total_pages}
          </span>
          <button
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
            disabled={pagination.current_page === pagination.total_pages}
            className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Submission Detail Modal */}
      {showModal && selectedSubmission && (
        <SubmissionModal
          submission={selectedSubmission}
          onClose={() => setShowModal(false)}
          onStatusUpdate={handleStatusUpdate}
        />
      )}
    </div>
  );
};

// Submission Detail Modal
const SubmissionModal: React.FC<{
  submission: ContactSubmission;
  onClose: () => void;
  onStatusUpdate: (id: number, status: string, adminNotes?: string) => Promise<void>;
}> = ({ submission, onClose, onStatusUpdate }) => {
  const [status, setStatus] = useState(submission.status);
  const [adminNotes, setAdminNotes] = useState(submission.admin_notes || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onStatusUpdate(submission.id, status, adminNotes);
      onClose();
    } catch (error) {
      // Error is handled in parent component
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              Contact Submission Details
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>

          <div className="space-y-6">
            {/* Submission Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Contact Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center">
                    <User className="w-4 h-4 text-gray-400 mr-2" />
                    <span>{submission.name}</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 text-gray-400 mr-2" />
                    <a href={`mailto:${submission.email}`} className="text-primary-600 hover:underline">
                      {submission.email}
                    </a>
                  </div>
                  {submission.phone && (
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 text-gray-400 mr-2" />
                      <a href={`tel:${submission.phone}`} className="text-primary-600 hover:underline">
                        {submission.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Event Information</h4>
                <div className="space-y-2 text-sm">
                  {submission.event_type && (
                    <div className="flex items-center">
                      <MessageSquare className="w-4 h-4 text-gray-400 mr-2" />
                      <span>{submission.event_type}</span>
                    </div>
                  )}
                  {submission.event_date && (
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      <span>{new Date(submission.event_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 text-gray-400 mr-2" />
                    <span>Submitted: {new Date(submission.created_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Message */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Message</h4>
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {submission.message}
                </p>
              </div>
            </div>

            {/* Status and Notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as 'new' | 'contacted' | 'booked' | 'cancelled')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="booked">Booked</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Notes
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Add internal notes..."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center"
            >
              {saving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )}
              Save Changes
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Contact;